// Atribución de campaña de la compra: UTM, fbclid y gclid.
//
// Llegan en la URL de la ficha del evento (el link de un anuncio o de una
// historia) y la compra se cierra varias páginas después, o en otra visita.
// Se guarda el último que llegó (last touch) por 30 días en localStorage y
// viaja en la orden. Sin esto la Radiografía del checkout no podía decir de
// qué campaña venía ninguna venta.

export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
] as const;

export type AttributionParam = typeof ATTRIBUTION_PARAMS[number];
export type CheckoutAttribution = Partial<Record<AttributionParam, string>>;

const STORAGE_KEY = 'avantti_attribution';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 500;

interface StoredAttribution {
  values: CheckoutAttribution;
  ts: number;
}

export const parseAttribution = (params: URLSearchParams | null | undefined): CheckoutAttribution => {
  const values: CheckoutAttribution = {};
  if (!params) {
    return values;
  }
  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) {
      values[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
  });
  return values;
};

// Se llama donde aterrizan los links: la ficha del evento y el embudo de compra.
// Una URL sin parámetros no borra lo guardado: navegar dentro del sitio no es
// un toque de campaña nuevo.
export const storeAttributionFromParams = (params: URLSearchParams | null | undefined, now = Date.now()): void => {
  const values = parseAttribution(params);
  if (Object.keys(values).length === 0) {
    return;
  }
  try {
    const payload: StoredAttribution = { values, ts: now };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Modo incógnito o storage lleno: se pierde la atribución, no la compra.
  }
};

export const readAttribution = (now = Date.now()): CheckoutAttribution => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    if (typeof parsed.ts !== 'number' || now - parsed.ts > MAX_AGE_MS || !parsed.values) {
      return {};
    }
    return parseAttribution(new URLSearchParams(
      Object.entries(parsed.values).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
    ));
  } catch {
    return {};
  }
};
